import { Link as RouterLink } from "react-router-dom";

import KeyOffIcon from "@mui/icons-material/KeyOff";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { SunoLogo } from "components/icons";

// On-brand, reassuring SSO error screen — used by /sso/error and by the
// /sso/complete fail-closed path. Keeps the Suno logo, a calm explanation, the
// specific (helpful) reason, and a single clear way forward, instead of a bare
// red alert. `data-testid="sso-callback-error"` is kept for the test contract.
export default function SsoErrorView({ message }: { message: string }) {
  const theme = useTheme();

  return (
    <Box
      data-testid="sso-callback-error"
      sx={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 3,
        py: 6
      }}
    >
      <Stack gap={4} alignItems="center" sx={{ maxWidth: 440, width: "100%" }}>
        <Box sx={{ "& g": { fill: theme.palette.text.primary } }}>
          <SunoLogo width={150} height={44} />
        </Box>

        <Stack
          gap={2}
          alignItems="center"
          sx={{
            width: "100%",
            p: 4,
            borderRadius: 3,
            bgcolor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            textAlign: "center"
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: theme.palette.action.hover,
              color: theme.palette.text.secondary
            }}
          >
            <KeyOffIcon fontSize="medium" />
          </Box>

          <Typography variant="h5" fontWeight={700}>
            We couldn&apos;t sign you in
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {message}
          </Typography>

          <Button
            component={RouterLink}
            to="/login"
            variant="contained"
            fullWidth
            sx={{ mt: 1 }}
          >
            Return to login
          </Button>
        </Stack>

        <Typography variant="caption" color="text.secondary">
          Still stuck? Contact your practice administrator.
        </Typography>
      </Stack>
    </Box>
  );
}
